import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { Text, View } from 'react-native';

import Link from 'components/link';
import Picture from 'components/picture';
import { useFormatCalories } from 'hooks/use-format-calories';
import { getStaticPathname } from 'navigation/location/location';
import { IMenuSectionItemProps } from 'pages/menu/components/menu-section/types';
import { emitTileClickedEvent } from 'pages/menu/components/menu-tile-grid/menu-tile/helpers';
import { buildMenuLink } from 'pages/menu/utils/navigation';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { useUIContext } from 'state/ui';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import { menuSectionStyles } from './styled';

// NEW one menu
const _MenuSectionListItem: React.FC<IMenuSectionItemProps> = ({ item }) => {
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const formatCalories = useFormatCalories();

  const linkPath = buildMenuLink(item);

  const handleLinkClick = useCallback(() => {
    const attributes = {
      component: !item.product
        ? ClickEventComponentNames.PRODUCT_SECTION_TILES
        : ClickEventComponentNames.PRODUCTS,
      name: item.name,
      componentId: item.id,
    };

    logButtonClick({ attributes }, { logDuplicateClickEvent: true });
    emitTileClickedEvent({
      pathname: getStaticPathname(),
      sanityId: item.id,
    });
  }, [item]);

  const handleOnImageLoaded = useCallback(() => {
    emitEvent(EventName.MENU_TILE_IMAGE_LOADED, {
      sanityId: item.id,
      isOnMainMenu: getStaticPathname() === routes.menu,
      attributes: [],
    });
  }, [item.id]);

  return (
    <Link key={item.id} onPress={handleLinkClick} linkPath={linkPath} dd-action-name={item.name}>
      <View style={menuSectionStyles.menuSectionRow} testID="menu-tile-bk">
        <View style={menuSectionStyles.menuSectionImageWrapper}>
          <Picture
            image={item.image.resource}
            alt={item.image.description}
            onImageLoaded={handleOnImageLoaded}
            accessibilityHint={formatMessage({ id: 'select' })}
            style={menuSectionStyles.menuSectionImage}
          />
        </View>
        <View style={menuSectionStyles.menuSectionContent}>
          <Text
            accessibilityRole="header"
            accessibilityLevel={2}
            style={menuSectionStyles.menuSectionTitle}
            testID={`tile-header-${item.name?.toLowerCase().replace(/\s/g, '')}`}
            numberOfLines={2}
          >
            {item.name}
          </Text>
          {item.product && (
            <View style={menuSectionStyles.menuSectionProductWrapper}>
              <Text style={menuSectionStyles.menuSectionItemPrice}>
                {formatCurrencyForLocale(item.product.price)}
              </Text>
              <Text style={menuSectionStyles.menuSectionItemCalories} numberOfLines={1}>
                {formatCalories(item.product.calories)}
              </Text>
            </View>
          )}
        </View>
      </View>
    </Link>
  );
};

export const MenuSectionListItem = React.memo(_MenuSectionListItem);
